import { ImgHTMLAttributes, ReactNode, useState } from 'react'
import styled from '@emotion/styled'
import { Box } from 'stylewhere/components'

interface Props extends ImgHTMLAttributes<any> {
  onEmptyImageComponent?: ReactNode
  variant?: 'boxed'
  transparent?: boolean
  maxHeightContainer?: number
  /** padding of the boxed variant, default '30px 20px' */
  boxPadding?: string
}

export const Image = (props: Props) => {
  const [src] = useState(props.src?.includes('assets/') ? `${process.env.PUBLIC_URL}/${props.src}` : props.src)
  const [error, seterror] = useState(!props.src)

  const onError = () => {
    if (!error) seterror(true)
  }

  const { onEmptyImageComponent, boxPadding, ...rest } = props
  // a percentage width: the image fills that share in width and takes its proportional height
  const fluidWidth = typeof rest.width === 'string' && rest.width.endsWith('%') ? rest.width : undefined
  const render = () => {
    if (error && onEmptyImageComponent) return onEmptyImageComponent
    if (error) {
      const height = rest.height ?? 100
      const width = fluidWidth ? '100%' : (rest.width ?? 100)
      return (
        <NoImageText
          transparent={props.transparent}
          center
          style={{
            height,
            minHeight: height,
            maxHeight: height,
            maxWidth: width,
            width,
            minWidth: width,
            ...rest.style
          }}
        >
          IMAGE
          <br />
          MISSING
        </NoImageText>
      )
    }
    return (
      <ImageCore
        style={
          fluidWidth
            ? { width: '100%', height: 'auto', maxHeight: props.maxHeightContainer, objectFit: 'contain' }
            : { width: rest.width ?? 100, height: rest.height ?? 100, objectFit: 'contain' }
        }
        src={src}
        onError={onError}
      />
    )
  }

  return (
    <Container
      vcenter
      // fluid: the cap is on the image itself, a capped container would clip it by its own padding
      maxHeight={fluidWidth ? undefined : props.maxHeightContainer}
      variant={props.variant}
      boxPadding={boxPadding}
      // fluid: the box hugs the image instead of stretching to the height of the row it sits in
      style={fluidWidth ? { width: fluidWidth, flexShrink: 0, alignSelf: 'flex-start' } : undefined}
    >
      {render()}
    </Container>
  )
}

const Container = styled(Box, { shouldForwardProp: (prop) => prop !== 'boxPadding' && prop !== 'theme' })<{
  variant?: 'boxed'
  maxHeight?: number
  boxPadding?: string
}>`
  border-radius: 10px;
  overflow: hidden;
  ${({ variant, maxHeight, boxPadding }) =>
    variant === 'boxed' &&
    ` padding: ${boxPadding ?? '30px 20px'};
      border: 1px solid #E0E0E0;
      box-sizing: border-box;
      border-radius: 5px;
      ${maxHeight ? `max-height: ${maxHeight}px;` : ''}
      align-items: center;`}
`

const ImageCore = styled.img``

const NoImageText = styled(Box)<{ transparent?: boolean }>`
  font-weight: 800;
  font-size: 10pt;
  text-align: center;
  color: rgba(0, 0, 0, 0.1);
  background-color: ${({ transparent, theme }) => (transparent ? 'transparent' : theme.background1)};
  border-radius: 10px;
  margin: auto;
`
